// Reversos de tarjeta (Chile) — F1a. Búsqueda de candidatos para el tab "Ajuste de tarjeta" de
// VincularMovimientoModal, según reversos-webpay-diseno-2026-09-20.md §3.2/§3.3/§3.4.
//
// Separación deliberada entre lógica PURA (testeable sin red) y el fetcher que arma sus insumos con
// supabase-js — mismo patrón que scripts/dualrun-pago.mjs usa para el ledger de proveedor.

export const VENTANA_DIAS_CANDIDATOS = 90;

export function restarDias(fechaISO, dias) {
  const d = new Date(`${fechaISO}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - dias);
  return d.toISOString().slice(0, 10);
}

// §3.2/§3.3/§3.4 — puro: recibe los candidatos ya traídos de la DB y decide qué mostrar.
// ajuste: { id, monto, tipo } (tipo de movimientos_bancarios: 'ingreso'|'egreso').
// candidatosAjuste: filas de movimientos_caja categoria_pago='ajuste_tarjeta' ya resueltas contra su
//   propio movimiento bancario ({ id, monto, descripcion, cuenta_er, fecha }) — pasar [] si
//   ajuste.tipo !== 'egreso' (§3.3 solo corre la pasada 1 para el patrón "Anulación").
// candidatosCompra: filas de movimientos_bancarios tipo='egreso' candidatas, con `movimiento_caja`
//   embebido (null si la compra sigue `pendiente`, sin fila de caja — §3.4).
export function resolverCandidatos({ ajuste, candidatosAjuste, candidatosCompra }) {
  const montoAbs = Math.abs(Number(ajuste.monto));

  const ajustesPrevios = (candidatosAjuste || []).filter(
    (c) => c.id !== ajuste.id && Math.abs(Number(c.monto)) === montoAbs
  );
  if (ajustesPrevios.length > 0) {
    // §3.3 pasada 1: ya hay un ajuste_tarjeta procesado con el mismo monto — el padre es ESE ajuste,
    // no la compra (cadena de 2 saltos hasta la compra real, ver §2 del diseño).
    return {
      tipo: "ajuste_previo",
      candidatos: ajustesPrevios.map((c) => ({
        movimientoCajaId: c.id,
        origen: "ajuste_tarjeta",
        fecha: c.fecha,
        descripcion: c.descripcion,
        cuentaEr: c.cuenta_er,
        monto: c.monto,
        elegibleF1a: true, // un ajuste ya procesado por este mismo mecanismo nunca hereda proveedor real.
      })),
      compraPendiente: [],
    };
  }

  // §3.2 pasada 2 (o directa, para el caso normal de reverso-ingreso).
  const conCaja = (candidatosCompra || []).filter(
    (cb) => cb.movimiento_caja && cb.movimiento_caja.categoria_pago !== "ajuste_tarjeta"
  );
  const sinCaja = (candidatosCompra || []).filter((cb) => !cb.movimiento_caja);

  return {
    tipo: "compra",
    candidatos: conCaja.map((cb) => ({
      movimientoCajaId: cb.movimiento_caja.id,
      movimientoBancarioId: cb.id,
      origen: "compra",
      fecha: cb.fecha,
      descripcion: cb.descripcion_raw,
      categoriaPago: cb.movimiento_caja.categoria_pago,
      cuentaEr: cb.movimiento_caja.cuenta_er,
      entidadTipo: cb.movimiento_caja.entidad_tipo,
      entidadId: cb.movimiento_caja.entidad_id,
      entidadNombre: cb.movimiento_caja.entidad_nombre,
      monto: cb.movimiento_caja.monto,
    })), // orden ya viene de la query (fecha DESC)
    compraPendiente: sinCaja.map((cb) => ({
      movimientoBancarioId: cb.id,
      fecha: cb.fecha,
      descripcion: cb.descripcion_raw,
    })),
  };
}

// Guard de alcance (contrapartida-2026-09-27.md §C/§E): un candidato "compra" con documento fiscal
// real detrás (MBF o evento de libro sobre su propio movimiento_bancario_id) es F1b, no F1a — se
// marca, nunca se descarta en silencio (10.177).
// mbfPorMovimiento / libroPorMovimiento: Map<movimiento_bancario_id, boolean>.
export function marcarNoElegibles(candidatos, { mbfPorMovimiento, libroPorMovimiento }) {
  return candidatos.map((c) => {
    if (c.origen !== "compra") return { ...c, elegibleF1a: true };
    const tieneDocumento =
      !!mbfPorMovimiento.get(c.movimientoBancarioId) || !!libroPorMovimiento.get(c.movimientoBancarioId);
    if (tieneDocumento) {
      return {
        ...c,
        elegibleF1a: false,
        motivoNoElegible: "la compra tiene documento fiscal real (MBF/libro) detrás — requiere F1b, no F1a",
      };
    }
    return { ...c, elegibleF1a: true };
  });
}

// Fetcher — arma los insumos de resolverCandidatos()/marcarNoElegibles() con supabase-js. Usado por
// el tab "Ajuste de tarjeta" (Commit 2, F1a) — Commit 1 solo lo mide, no lo cablea a UI.
export async function buscarCandidatosAjusteTarjeta(client, ajuste) {
  const { id, monto, tipo, cuenta_bancaria_id: cuentaBancariaId, pais, fecha } = ajuste;
  const fechaMin = restarDias(fecha, VENTANA_DIAS_CANDIDATOS);

  // §3.3: la pasada 1 (ajustes ya procesados) solo corre para el patrón "Anulación" (egreso).
  let ajustePrevioQuery = Promise.resolve({ data: [], error: null });
  if (tipo === "egreso") {
    ajustePrevioQuery = client
      .from("movimientos_caja")
      .select("id, monto, descripcion, cuenta_er, movimientos_bancarios!inner(fecha,cuenta_bancaria_id,pais)")
      .eq("categoria_pago", "ajuste_tarjeta")
      .eq("movimientos_bancarios.cuenta_bancaria_id", cuentaBancariaId)
      .eq("movimientos_bancarios.pais", pais)
      .lte("movimientos_bancarios.fecha", fecha)
      .gte("movimientos_bancarios.fecha", fechaMin);
  }

  const [ajusteRes, compraRes] = await Promise.all([
    ajustePrevioQuery,
    client
      .from("movimientos_bancarios")
      .select(
        "id, fecha, descripcion_raw, movimiento_caja:movimientos_caja(id,categoria_pago,cuenta_er,entidad_tipo,entidad_id,entidad_nombre,monto)"
      )
      .eq("tipo", "egreso")
      .eq("monto", monto)
      .eq("cuenta_bancaria_id", cuentaBancariaId)
      .eq("pais", pais)
      .neq("id", id)
      .not("descripcion_raw", "ilike", "%reverso%")
      .not("descripcion_raw", "ilike", "%anulaci%")
      .lte("fecha", fecha)
      .gte("fecha", fechaMin)
      .order("fecha", { ascending: false }),
  ]);
  if (ajusteRes.error) throw ajusteRes.error; // 10.170 — nunca tragar error a [].
  if (compraRes.error) throw compraRes.error;

  const candidatosAjuste = (ajusteRes.data || []).map((c) => ({
    id: c.id,
    monto: c.monto,
    descripcion: c.descripcion,
    cuenta_er: c.cuenta_er,
    fecha: c.movimientos_bancarios?.fecha,
  }));
  const candidatosCompra = (compraRes.data || []).map((cb) => ({
    id: cb.id,
    fecha: cb.fecha,
    descripcion_raw: cb.descripcion_raw,
    movimiento_caja: Array.isArray(cb.movimiento_caja) ? cb.movimiento_caja[0] || null : cb.movimiento_caja,
  }));

  const resultado = resolverCandidatos({ ajuste, candidatosAjuste, candidatosCompra });
  if (resultado.tipo !== "compra" || resultado.candidatos.length === 0) return resultado;

  const movimientoBancarioIds = resultado.candidatos.map((c) => c.movimientoBancarioId);
  const [{ data: mbfRows, error: errMbf }, { data: libroRows, error: errLibro }] = await Promise.all([
    client
      .from("movimiento_bancario_facturas")
      .select("movimiento_bancario_id")
      .in("movimiento_bancario_id", movimientoBancarioIds),
    client
      .from("aplicaciones_saldo")
      .select("origen_id")
      .eq("origen_tipo", "movimiento_bancario")
      .in("origen_id", movimientoBancarioIds),
  ]);
  if (errMbf) throw errMbf;
  if (errLibro) throw errLibro;

  const mbfPorMovimiento = new Map((mbfRows || []).map((r) => [r.movimiento_bancario_id, true]));
  const libroPorMovimiento = new Map((libroRows || []).map((r) => [r.origen_id, true]));

  return {
    ...resultado,
    candidatos: marcarNoElegibles(resultado.candidatos, { mbfPorMovimiento, libroPorMovimiento }),
  };
}
